import React, { Suspense, useMemo } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import "./i18n";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { SearchFnProvider } from "./search/search";
import { m365OrJiraOrAllSearch } from "./search/all.searchs";
import { defaultSearchContext } from "./search/searchContext";
import axios from "axios";
import { envName } from "./util/common-util";
import { AiRagProvider } from "./components/ai/contexts/ai.rag.context";
import { defaultGraphApiTokenFn } from "./search/graph.api.search";
import { getKIDEmail } from "./util/auth";

import { WindowProvider } from "./context/WindowContext";
import { defaultUniqueUsersFn, UniqueUsersConfigFn, UniqueUsersProvider } from "./components/unique-users/unique.users";
import { defaultJqlConfig, JqlAiClientConfig, makeJqlAiClient } from "./jira/jql.client.agent";
import { EnvironmentConfig, LLM_MODEL } from "@me8eon/config";
import { JqlOpenAiClient, openAiClientForJql, OpenAiConfigForJql } from "./jira/ai/ai.client.for.jql";
import { jqlDslYaml } from "./jira/jql.prompt.yaml";
import { defaultJiraTokenFn } from "./jira/jira.token.management";
import { defaultOauth2Config } from "./jira/jira.consent.popup";
import { aiRagDebug, AiSourceDataMemoryProvider } from "./components/ai/clients/ai.rag.client";
import { AiAssistantProvider, emptyAiAssistScaffolding } from "@me8eon/ai_assist_data_widget";
import { themeFF, ThemeProvider } from "@me8eon/themes";
import { allThemes, themeFeatureFlag } from "@me8eon/all_themes";
import { FeatureFlags, FeatureFlagsStateProvider, useFeatureFlag } from "@me8eon/featureflags";
import { dataAsJsonFF, DataPluginProvider } from "@me8eon/data";
import { debugSearchResultsName } from "@me8eon/data_views_search_results";
import { EmptyFilterScaffolding, filterDebugName, FilterProvider, FilterScaffoldingProvider, searchDebugName } from "@me8eon/filter_data_widget";
import { WindowUrlProvider } from "@me8eon/routing";

import { LanguageProvider } from "@me8eon/language";
import { allTranslations } from "@me8eon/translate_eon";
import { TranslationUsedAndNotFoundProvider } from "@me8eon/translation";
import { SimpleTranslationProvider } from "@me8eon/simple_translation";
import { DevModeAuthentication, DevModeBanner, DevModeComponents, DevModeComponentsProvider, DevModeDebug, DevModeFeatureFlags, DevmodeFilters, DevModeStateForSearchProvider, DevModeTranslate, simpleDevModeComponents } from "@me8eon/devmode";
import { DebugStateProvider, makeDebugLog } from "@me8eon/debug";
import { dataViewFilterName, DataViewNavBarLayoutProvider, DataViewsProvider, SimpleDataNavBarViewLayout } from "@me8eon/data_views";
import { allDataViews } from "@me8eon/all_data_views";
import { DataWidgetProvider } from "@me8eon/data_widget";
import { allDataWidgets } from "@me8eon/all_data_widgets";
import { allDataPlugins } from "@me8eon/all_data";
import { AttributeEditorProvider } from "@me8eon/editors";
import { allEditors } from "@me8eon/all_editors";
import { AttributeValueProvider, SimpleAttributeValueLayout, SimpleDataLayout } from "@me8eon/renderers";
import { allRenderers } from "@me8eon/all_renderers";
import { defaultFeedbackFn, DisplayFeedbackComponentProvider, FeedbackProvider } from "@me8eon/feedback";
import { Feedback } from "./components/feedback/feedback.component";
import { jqlDebugName, JqlFeatureFlag, jqlFeatureFlagName } from "./hooks/useJqlFlag";
import { AccessTokenScaffoldingProvider, Authenticate, authenticateDebugName, AuthenticationProvider, useUserDataEmail } from "@me8eon/react_authentication";
import { loginUsingMsal } from "@me8eon/msal_authentication";
import { AllSearchDataProvider, DevModeSearch, SearchParserConfig, SearchParserConfigProvider, searchParserFeature, searchParserFFName, SearchResultsProvider } from "@me8eon/search";
import { DoTheSearching, emptySearchData } from "@me8eon/all_search";
import { CommonComponentsProvider } from "@me8eon/components";
import { DataSourcesProvider, TokenToDataSourcesFn } from "@me8eon/data_sources";
import { allDataSources } from "@me8eon/all_data_sources";
import { FilterPluginsProvider, GuiFilterProvider } from "@me8eon/filters";
import { allFilters } from "@me8eon/all_filters";
import { elasticSearchAccessTokenConfig, ElasticSearchContext } from "@me8eon/elastic_search_data_sources";
import { GraphApiContext } from "@me8eon/graph_api_data_sources";
import { searchTextName } from "@me8eon/searchtext_filters";
import { commonComponentsFn } from "@me8eon/allcomponents";
import { createTeamsLoginConfig, isInTeams } from "@me8eon/teams_authentication";
import { AccessTokenConfig, AccessTokenStoreConfig, AccessTokenStoreProvider, createTokenStore, getToken, jwtTokenDetails, jwtTokenName } from "@me8eon/access_token";
import { decodeToken, JwtTokenFn, LoginConfig } from "@me8eon/authentication";
import { calculateTokenRefreshTime, dateTimeService, TimeService } from "@me8eon/time";
import { msalGraphApiAccessTokenConfig } from "@me8eon/msal_authentication/src/msal.access.tokens";
import { EventMessage, EventType, PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./msalConfig";
import { App } from "./App";
import { ExplainControlCenter, ExplainProvider } from "@me8eon/diagnostics";
import { CryptoStoreProvider, getWorkingStorage, StorageProvider } from "@me8eon/react_storage";
import { eventStoreDebug } from "@me8eon/react_event_storage";
import { AiSessionData, makeMessageAndSessionsStoreFromAsyncStorage, makeMessageAndSessionsStoreFromIStorage, MessageWithActionAndData, SessionAndMessagesProvider, sessionsAndMessagesDebugName, SessionsDevMode } from "@me8eon/sessions_messages";
import { AsyncStore, defaultRandomId } from "@me8eon/async_storage";
import { AiAssistProvider } from "./components/ai-assist-refactored/AiAssist.context";
import { proxyBlobStorage, ProxyBlobStorageConfig, createCryptoClient, CryptoClient, CryptoClientConfig } from "@me8eon/azure_async_storage";
import { toGitStoragePath } from "@me8eon/string_utils";
import { AlertProvider } from "@me8eon/alert";
import { jsonCodec } from "@me8eon/codec";
import { CryptoMetadata } from "@enterprise_search/storage_crypto";
import { azureEncryptedBlobStore, createCryptoStore, CryptoStore } from "@me8eon/crypto_store";
import { AiClients } from "@me8eon/aiclient";
import { azureOpenAiClient, defaultAzureAiConfig } from "@me8eon/azureai";
import {
    agentCards as agentCardsGpt,
    aiToolsRepository,
    allExecutors as allExecutorsGpt,
    allSelectors as allSelectorsGpt,
} from "@me8eon/all_agent";
import { allExecutors as allExecutorsGemini, allSelectors as allSelectorsGemini } from "@me8eon/all_agent/src/all.intent";
import { agentCards as agentCardsGemini } from "@me8eon/all_agent/src/all.cards";
import {
    AgentContextType, AiAgentAndPipelineResultsProvider, IntentDevMode,
    AgentSelectorContextProvider, AiStreamingUiStateProvider, emptyAiStreamingUiState,
    useSelectedLlmModel, SelectedLlmModelProvider
} from "@me8eon/react_intent";
import useCompassStore from "./store/compassStore";
import { compassPersonalizationFeatureFlag, compassPersonalizationFFName } from "./features/AiPrompt/plugins/CompassSourceSelectorTextAreaPlugin/compassPersonalization.feature.flag";
import { LoadingFlagProvider, QuestionsSuggestionsProvider } from "@me8eon/async";
import { metaHumanFeatureFlag, metaHumanFFName } from "./pages/communicate/audio.feature.flag";
import { managedNetworkFeatureFlag, managedNetworkFFName } from "./modules/AiWidgets/nexthink/managedNetwork.feature.flag";
import { setUpRandomForBrowser } from "@enterprise_search/random_node_and_browser";
import { defaultElasticSearchToolConfig } from "@me8eon/ai.tools.utils";
import { createOpenAiClient } from "@me8eon/openai/src/openai.client";
import { createOpenAiClient as createOpenAiClientGemini } from "@me8eon/openai/src/openai.client";
import { AgentContextProvider } from "@me8eon/react_intent/src/context/AgentContext";
import { llmModelSelectorFFName, LlmModelSelectorFeatureFlag } from "./hooks/useLlmModelSelectorFlag";
import { MsalProvider } from "@azure/msal-react";

setUpRandomForBrowser();

let agentContextFactory: ((model: string) => AgentContextType<any>) | undefined;
let staticAgentContext: AgentContextType<any> | undefined;

function DynamicAgentContextWrapper({ children }: {
    children: React.ReactNode;
}) {
    const llmSelectorEnabled = useFeatureFlag(llmModelSelectorFFName);
    const [selectedModel] = useSelectedLlmModel();

    const agentContext = useMemo(() => {
        if (llmSelectorEnabled) {
            // Feature flag ON: use dynamic factory based on selected model
            return agentContextFactory?.(selectedModel);
        } else {
            // Feature flag OFF: use static context (original behavior with env var model)
            return staticAgentContext;
        }
    }, [llmSelectorEnabled, selectedModel]);

    return agentContext ? <AgentContextProvider intent={agentContext}>{children}</AgentContextProvider> : null;
}

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
);

export const searchContext = defaultSearchContext(axios);
const diagnostics = window.location.href.includes("diagnostics");
export const msalInstance: PublicClientApplication = new PublicClientApplication(msalConfig);
msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE || event.eventType === EventType.SSO_SILENT_FAILURE) {
        console.warn("Silent token renewal failed:", event.error);
    }
});
const graphApiTokenFn: () => Promise<string> = defaultGraphApiTokenFn(
    [`https://graph.microsoft.com/Sites.Read.All`],
    msalInstance,
);

const uniqueUsersFn: UniqueUsersConfigFn = defaultUniqueUsersFn(axios, envName);

const featureFlags: FeatureFlags = {
    // ["myhr"]: { value: false, description: "myhr feature flag for chatbot" },
    [dataAsJsonFF]: { value: false, description: "show data as json" },
    [jqlFeatureFlagName]: JqlFeatureFlag,
    [themeFF]: themeFeatureFlag,
    [searchParserFFName]: searchParserFeature,
    [metaHumanFFName]: metaHumanFeatureFlag,
    [compassPersonalizationFFName]: { ...compassPersonalizationFeatureFlag, value: window.location.href.includes("compassPersonalization=true") },
    [llmModelSelectorFFName]: LlmModelSelectorFeatureFlag,
    [managedNetworkFFName]: managedNetworkFeatureFlag
};

const debugState = {
    [aiRagDebug]: false,
    [authenticateDebugName]: false,
    [debugSearchResultsName]: false,
    [sessionsAndMessagesDebugName]: true,
    [eventStoreDebug]: false,
    [filterDebugName]: false,
    [jqlDebugName]: false,
    [searchDebugName]: false,
};

const elasticSearchContext = (token: JwtTokenFn): ElasticSearchContext => ({
    timeService: () => new Date(),
    elasticSearchUrl: EnvironmentConfig.elasticSearchProxyUrl,
    axios,
    token,
    indexBoosts: {
        "order-it-api": {
            boost: 2,
            terms: [
                "order", "procure", "buy", "purchase",
                "bestellen", "beschaffen", "kaufen", "erwerben",
                "beställa", "anskaffa", "köpa", "köpa",
            ],
        },
        "mygenius-api": {
            boost: 2,
            terms: [
                "training", "trainings", "train", "learn", "learning", "enroll", "course", "courses",
                "Training", "Schulungen", "trainieren", "lernen", "Lernen", "einschreiben", "Kurs", "Kurs",
                "utbildning", "utbildningar", "träna", "lära sig", "inlärning", "registrera dig", "kurs", "kurser",
            ],
        },
    },
});

const graphApiContext = (token: JwtTokenFn): GraphApiContext => ({
    graphApiUrl: "https://graph.microsoft.com/v1.0/search/query",
    token,
});

const dataSources: TokenToDataSourcesFn = (dlsTokenFn, graphApiToken, activeFilter) =>
    allDataSources<any>(elasticSearchContext(dlsTokenFn), graphApiContext(graphApiToken), activeFilter);

const devModeComponents: DevModeComponents = {
    Hide: () => <></>,
    Debug: DevModeDebug,
    FeatureFlags: DevModeFeatureFlags,
    Translate: DevModeTranslate,
    Authentication: DevModeAuthentication,
    Filters: DevmodeFilters,
    Intent: IntentDevMode,
    Sessions: SessionsDevMode,
    Search: DevModeSearch,
    Explain: ExplainControlCenter,
    Banner: DevModeBanner,
};

const initialFilters = {
    [searchTextName]: window.location.pathname === "/search-result" ? new URL(window.location.href).searchParams.get("q") || "" : "",
    [dataViewFilterName]: "all",
};

let loginConfig: LoginConfig | null = null;

// Late-binding ES API key function, set once DLS token is available
export let esApiKeyFn: JwtTokenFn = async () => { throw new Error("ES API key not yet initialized"); };

export const aiConfig: OpenAiConfigForJql = {
    axios,
    jwtTokenFn: () => loginConfig!.jwtToken(),
    // Backend-node-v2's own completions proxy (/service/completions) — it injects the AI
    // gateway credential server-side, so the browser no longer forwards its own JWT to a
    // third party as "x-proxy-token".
    baseURL: EnvironmentConfig.nodeService,
    customisation: {
        model: LLM_MODEL
    },
    debug: false,
};


export const ai: JqlOpenAiClient = openAiClientForJql(aiConfig);
export const jqlConfig: JqlAiClientConfig<any> = defaultJqlConfig(jqlDslYaml, ai, defaultJiraTokenFn(defaultOauth2Config), true);
export const jqlAiClient = makeJqlAiClient(jqlConfig);
const searchFn = m365OrJiraOrAllSearch(
    searchContext,
    graphApiTokenFn,
    // defaultJiraTokenFn([`https://jira.eon.com/.default`], msalInstance)
    // defaultJiraTokenFn([`https://jira.eon.com/.default`], msalInstance),
    jqlAiClient,
);


async function getOnBehalfOf(
    teamsIdToken: string,
    scopes: string[],
) {
    const tenantId = "b914a242-e718-443b-a47c-6b4c649d8c0a";
    const client_id = process.env.REACT_APP_K8S_MSAL_CLIENT_ID || "";
    const url = `${EnvironmentConfig.gatewayURL}/proxy/${tenantId}/oauth2/v2.0/token`;
    const params = new URLSearchParams({
        client_id,
        // client_secret: clientSecret, this will be added by the proxy
        grant_type: "urn:ietf:params:oauth:grant-type:jwt-bearer",
        requested_token_use: "on_behalf_of",
        assertion: teamsIdToken,
        scope: scopes.join(" "),
    });

    try {
        const response = await axios.post(url, params, {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "x-proxy": EnvironmentConfig.entraidOboProxy,
                "x-proxy-token": await jwtTokenFn(),
            },
        });
        return response.data; // Contains the access token
    } catch (error) {
        console.error("Error fetching token:", axios.isAxiosError(error) ? error.response?.data : error);
        throw error;
    }
}

function teamsGraphApiAccessTokenFn(scopes: string[], timeService: TimeService = dateTimeService): AccessTokenConfig {
    return {
        fn: async (debug, login, storage, timeService) => {
            const loginToken = await login(debug);
            if (!loginToken) return undefined;
            const accessTokenData = await getOnBehalfOf(loginToken, scopes);
            debug("teamsGraphApiAccessTokenFn", "accessTokenData", accessTokenData);
            const { access_token } = accessTokenData;
            const decoded = decodeToken(access_token);
            const expiry = calculateTokenRefreshTime(timeService, decoded.iat, decoded.exp);
            return { token: access_token, expiry };
        },
        timeService,
        ttl: 50 * 60 * 1000, //50 mins (the token actually lives for 60)
    };
}

const teamsAccessStoreConfig: AccessTokenStoreConfig = {
    [jwtTokenName]: jwtTokenDetails,
    dls: elasticSearchAccessTokenConfig(EnvironmentConfig.apikeyUrl),
    trueDemApi: teamsGraphApiAccessTokenFn([`https://analytics.api.truedem.app/.default`]),
    graphApi: teamsGraphApiAccessTokenFn(["https://graph.microsoft.com/.default"]),
    profileImages: teamsGraphApiAccessTokenFn([`https://graph.microsoft.com/User.ReadBasic.All`]),
};


const browserAccessStoreConfig: AccessTokenStoreConfig = {
    [jwtTokenName]: jwtTokenDetails,
    dls: elasticSearchAccessTokenConfig(EnvironmentConfig.apikeyUrl),
    trueDemApi: msalGraphApiAccessTokenConfig(msalInstance, [`https://analytics.api.truedem.app/.default`]),
    graphApi: msalGraphApiAccessTokenConfig(msalInstance, [`https://graph.microsoft.com/Sites.Read.All`]),
    profileImages: msalGraphApiAccessTokenConfig(msalInstance, [`https://graph.microsoft.com/User.ReadBasic.All`]),
};

function getAccessStoreConfig(lc: LoginConfig) {
    if (lc.type === "teams") return teamsAccessStoreConfig;
    if (lc.type === "msal") return browserAccessStoreConfig;
    else throw new Error(`Unknown login config type. [${lc.type}]. Legal values are teams and msal`);
}

const searchParserConfig: SearchParserConfig = {
    indexWords: {
        aiAssistant: ["ai", "assistant", "aiassistant"],
        all: "all",
        apps: ["apps"],
        confluence: "confluence",
        emails: ["email", "emails", "mail"],
        jira: "jira",
        mygenius: ["training", "mygenius"],
        orderit: ["orderit", "itorder", "orders", "myorders"],
        people: "people",
        gitlab: "gitlab",
        m365: ["sharepoint", "onedrive", "files"],
    },
    fromWords: ["from", "in"],
    questionWords: ["what", "why", "how"],
    aiNames: ["lucy", "ai", "jane", "chatbot"],
    orderWords: ["order"],
};

const storage = getWorkingStorage();

function defaultAiSessionData(random: () => string, timeService: TimeService): AiSessionData {
    const childId = random();
    return {
        sessions: [{ id: childId, name: "", time: timeService().toISOString() }],
        selected: childId,
    };
}

const cryptoClientConfig: CryptoClientConfig = {
    axios,
    base: EnvironmentConfig.gatewayURL,
};
const cryptoClient: CryptoClient = createCryptoClient(cryptoClientConfig);
const jwtTokenFn: JwtTokenFn = async () => loginConfig?.jwtToken();

const azureConfig = <T,>(prefix: string, defaultValue: T, context: string): ProxyBlobStorageConfig<string, T> => ({
    base: EnvironmentConfig.nodeService,
    jwtTokenFn,
    defaultValue: async () => ({ value: defaultValue }),
    blobNameFn: (id) => {
        if (!id || id.length < 5) {
            return `${EnvironmentConfig.recentChatEnvPrefix}/${prefix}/invalid-id`;
        }
        const kid = getKIDEmail();
        if (id && id === kid) {
            return `${EnvironmentConfig.recentChatEnvPrefix}/${prefix}`;
        }
        return `${EnvironmentConfig.recentChatEnvPrefix}/${prefix}/${toGitStoragePath(id)}`;
    },
    axios,
    codec: jsonCodec(context),
});

// Metadata store (for crypto): CryptoMetadata
const metaStore = proxyBlobStorage<string, CryptoMetadata>(azureConfig("meta", {} as any, "meta"));
const cryptoStore = createCryptoStore({
    jwtTokenFn,
    cryptoClient,
    rawStore: metaStore,
    metaCache: new Map(),
    secretCache: new Map(),
});

export function createMessageAndSessionsStoreUsingFeatureFlag() {
    const ff = !window.location.href.includes("blobStore=false");
    if (ff) {


        // Data store (encrypted payloads): string
        const rawSessionDataStore = proxyBlobStorage<string, string>(azureConfig("data", "" as any, "raw session"));
        const rawMessageDataStore = proxyBlobStorage<string, string>(azureConfig("data", "" as any, "raw message"));

        const sessionStore: AsyncStore<string, AiSessionData> = azureEncryptedBlobStore({
            cryptoStore,
            rawStore: rawSessionDataStore,
            codec: jsonCodec("session"),
            defaultValue: () => ({ value: defaultAiSessionData(defaultRandomId, dateTimeService) }),
        });

        const messagesStore: AsyncStore<string, MessageWithActionAndData[]> = azureEncryptedBlobStore({
            cryptoStore,
            rawStore: rawMessageDataStore,
            codec: jsonCodec("messages"),
            defaultValue: () => ({ value: [] }),
        });

        const random = defaultRandomId;
        const timeService = dateTimeService;


        return makeMessageAndSessionsStoreFromAsyncStorage({
            sessionStore,
            messagesStore,
            random,
            timeService,
        });
    } else
        return makeMessageAndSessionsStoreFromIStorage({
            sessionStorage: storage,
            messagesStorage: storage,
            random: defaultRandomId,
            timeService: dateTimeService,
        });
}

export const { messageAndSessionsStore } = createMessageAndSessionsStoreUsingFeatureFlag();


if (window.location.href.endsWith("/ff.json")) {
    root.render(<pre id="json">{JSON.stringify(featureFlags, null, 2)}</pre>);
} else
    isInTeams().then(it => {
        return it
            ? createTeamsLoginConfig()
            : msalInstance.initialize({}).then(() => loginUsingMsal({ msal: msalInstance }));
    }).then(lc => {
        loginConfig = lc;
        const accessStoreConfig = getAccessStoreConfig(lc);
        const accessTokenStore = createTokenStore(accessStoreConfig);
        if (window.location.href.endsWith("/ff.json")) {
            root.render(<pre>{JSON.stringify(featureFlags, null, 2)}</pre>);
        }
        const azureAiClientInstance = azureOpenAiClient(defaultAzureAiConfig(axios, `${EnvironmentConfig.gatewayURL}/proxy`, () => loginConfig!.jwtToken()));

        const createAiClientsWithModel = (model: string): AiClients => {
            const isGemini = model.toLowerCase().includes("gemini");
            return {
                azureai: azureAiClientInstance,
                openai: isGemini
                    ? createOpenAiClientGemini({
                        axios,
                        baseURL: EnvironmentConfig.nodeService,
                        apiKey: () => loginConfig!.jwtToken(),
                        defaultModel: model,
                    })
                    : createOpenAiClient({
                        axios,
                        baseURL: EnvironmentConfig.nodeService,
                        apiKey: () => loginConfig!.jwtToken(),
                        defaultModel: model,
                    }),
            };
        };

        const createAgentContextWithModel = (model: string): AgentContextType<any> => {
            const aiClients = createAiClientsWithModel(model);
            return {
                selFns: allSelectorsGpt(aiClients, toolRepo),
                cards: agentCardsGpt,
                executors: allExecutorsGpt(aiClients, toolRepo),
                selectedAgent: undefined,
            };
        };


        const getDlsToken: JwtTokenFn = () =>
            getToken(accessTokenStore)("dls", loginConfig?.refreshLogin!, storage, makeDebugLog(debugState, ""));
        esApiKeyFn = getDlsToken;
        const graphApiTokenFn: JwtTokenFn = () => getToken(accessTokenStore)("graphApi", loginConfig?.refreshLogin!, storage, makeDebugLog(debugState, ""));
        const trueDemApiTokenFn: JwtTokenFn = () => getToken(accessTokenStore)("trueDemApi", loginConfig?.refreshLogin!, storage, makeDebugLog(debugState, ""));
        const esConfig = defaultElasticSearchToolConfig(EnvironmentConfig.elasticSearchProxyUrl, getDlsToken);
        const toolRepo = aiToolsRepository({
            elasticSearchConfig: esConfig,
            jiraTokenFn: defaultJiraTokenFn(defaultOauth2Config),
            graphApiTokenFn,
            trueDemApiTokenFn,
            jwtTokenFn: () => loginConfig!.jwtToken(),
            userEmail: getKIDEmail(),  // Pass user email for ACL-enabled search tools
            scopeFn: () => useCompassStore.getState().compassScope
        });
        // const aiExecutorsForAgents = USE_GEMINI_FLOW
        //     ? allExecutorsGemini(aiClientsForAgents, toolRepo, () => loginConfig!.jwtToken())
        //     : allExecutorsGpt(aiClientsForAgents, toolRepo, () => loginConfig!.jwtToken());
        // const agentContext: AgentContextType<any> = {
        //     selFns: USE_GEMINI_FLOW
        //         ? allSelectorsGemini(aiClientsForAgents, toolRepo)
        //         : allSelectorsGpt(aiClientsForAgents, toolRepo),
        //     cards: USE_GEMINI_FLOW ? agentCardsGemini : agentCardsGpt,
        //     executors: aiExecutorsForAgents,
        //     selectedAgent: undefined,
        // };
        agentContextFactory = createAgentContextWithModel;
        staticAgentContext = createAgentContextWithModel(LLM_MODEL);
        root.render(
            <React.StrictMode>
                <MsalProvider instance={msalInstance}>
                    <LoadingFlagProvider loadingFlag={false}>
                        <QuestionsSuggestionsProvider questionsSuggestions={[]}>
                            <StorageProvider storage={storage}>
                                <CryptoStoreProvider cryptoStore={cryptoStore}>
                                    <DisplayFeedbackComponentProvider displayFeedbackComponent={Feedback}>
                                        <WindowUrlProvider>
                                            <SearchParserConfigProvider searchParserConfig={searchParserConfig}>
                                                <AccessTokenStoreProvider tokenStore={accessTokenStore}>
                                                    <GuiFilterProvider guiFilters={initialFilters}>
                                                        <SearchResultsProvider searchResults={[]}>
                                                            <FilterScaffoldingProvider>
                                                                <FilterProvider filter={EmptyFilterScaffolding}>
                                                                    <AiAssistantProvider aiAssist={emptyAiAssistScaffolding}>
                                                                        <DebugStateProvider debugState={debugState}>
                                                                            <AuthenticationProvider loginConfig={loginConfig}>
                                                                                <LanguageProvider language={localStorage.getItem("i18nextLng")?.slice(0, 2) || Object.keys(allTranslations)[0]}>
                                                                                    <TranslationUsedAndNotFoundProvider usedAndNotFound={{ used: new Set(), notFound: new Set(), errors: new Set() }}>
                                                                                        <SimpleTranslationProvider languageLookup={allTranslations}>
                                                                                            <FeatureFlagsStateProvider featureFlags={featureFlags}>
                                                                                                <DevModeStateForSearchProvider devModeState={{ selected: "" }}>
                                                                                                    <DevModeComponentsProvider components={simpleDevModeComponents(devModeComponents)}>
                                                                                                        <CommonComponentsProvider componentsFn={commonComponentsFn}>
                                                                                                            <DataViewNavBarLayoutProvider dataViewLayout={SimpleDataNavBarViewLayout}>
                                                                                                                <AllSearchDataProvider allSearchData={emptySearchData({})}>
                                                                                                                    <ThemeProvider themes={allThemes}>
                                                                                                                        <FilterPluginsProvider filterPlugins={allFilters}>
                                                                                                                            <DataSourcesProvider dataSources={dataSources} msalInstance={msalInstance}>
                                                                                                                                <SelectedLlmModelProvider selectedLlmModel={LLM_MODEL}>
                                                                                                                                    <DynamicAgentContextWrapper>
                                                                                                                                        <AgentSelectorContextProvider agent={undefined}>
                                                                                                                                            <AiStreamingUiStateProvider aiStreamingUiState={emptyAiStreamingUiState}>
                                                                                                                                                <AiAgentAndPipelineResultsProvider aiAgentAndPipelineResults={{}}>
                                                                                                                                                    <DataViewsProvider dataViews={allDataViews}>
                                                                                                                                                        <DataWidgetProvider dataWidgets={allDataWidgets}>
                                                                                                                                                            <DataPluginProvider dataPlugins={allDataPlugins}>
                                                                                                                                                                <AttributeEditorProvider editors={allEditors} AttributeEditorLayout={SimpleAttributeValueLayout} DataLayout={SimpleDataLayout}>
                                                                                                                                                                    <AttributeValueProvider renderers={allRenderers} AttributeValueLayout={SimpleAttributeValueLayout} DataLayout={SimpleDataLayout}>
                                                                                                                                                                        <AiSourceDataMemoryProvider>
                                                                                                                                                                            <WindowProvider>
                                                                                                                                                                                <UniqueUsersProvider uniqueUsers={uniqueUsersFn}>
                                                                                                                                                                                    <ExplainProvider context={elasticSearchContext}>
                                                                                                                                                                                        <SessionAndMessagesProvider useId={useUserDataEmail} sessionsAndMessages={messageAndSessionsStore}>
                                                                                                                                                                                            <FeedbackProvider feedbackFn={defaultFeedbackFn(EnvironmentConfig.nodeService, envName, axios, "feedback")}>
                                                                                                                                                                                                <SearchFnProvider searchFn={searchFn}>
                                                                                                                                                                                                    <AiRagProvider>
                                                                                                                                                                                                        <Authenticate LoadingDisplay={() => <div />}>
                                                                                                                                                                                                            <AccessTokenScaffoldingProvider>
                                                                                                                                                                                                                <DoTheSearching>
                                                                                                                                                                                                                    <AiAssistProvider>
                                                                                                                                                                                                                        <AlertProvider>
                                                                                                                                                                                                                            <Suspense
                                                                                                                                                                                                                                fallback={
                                                                                                                                                                                                                                    <EonUiPreloaderSquares
                                                                                                                                                                                                                                        className="pre-loader-gif"
                                                                                                                                                                                                                                    ></EonUiPreloaderSquares>
                                                                                                                                                                                                                                }
                                                                                                                                                                                                                            >
                                                                                                                                                                                                                                <App msalInstance={msalInstance} />{/* //TODO remove msalInstance when have sorted out the Header code */}
                                                                                                                                                                                                                            </Suspense>
                                                                                                                                                                                                                        </AlertProvider>
                                                                                                                                                                                                                    </AiAssistProvider>
                                                                                                                                                                                                                </DoTheSearching>
                                                                                                                                                                                                            </AccessTokenScaffoldingProvider>
                                                                                                                                                                                                        </Authenticate>
                                                                                                                                                                                                    </AiRagProvider>
                                                                                                                                                                                                </SearchFnProvider>
                                                                                                                                                                                            </FeedbackProvider>
                                                                                                                                                                                        </SessionAndMessagesProvider> </ExplainProvider>
                                                                                                                                                                                </UniqueUsersProvider>
                                                                                                                                                                            </WindowProvider>
                                                                                                                                                                        </AiSourceDataMemoryProvider>
                                                                                                                                                                    </AttributeValueProvider>
                                                                                                                                                                </AttributeEditorProvider>
                                                                                                                                                            </DataPluginProvider>
                                                                                                                                                        </DataWidgetProvider>
                                                                                                                                                    </DataViewsProvider>
                                                                                                                                                </AiAgentAndPipelineResultsProvider>
                                                                                                                                            </AiStreamingUiStateProvider>
                                                                                                                                        </AgentSelectorContextProvider>
                                                                                                                                    </DynamicAgentContextWrapper>
                                                                                                                                </SelectedLlmModelProvider>
                                                                                                                            </DataSourcesProvider>
                                                                                                                        </FilterPluginsProvider>
                                                                                                                    </ThemeProvider>
                                                                                                                </AllSearchDataProvider>
                                                                                                            </DataViewNavBarLayoutProvider>
                                                                                                        </CommonComponentsProvider>

                                                                                                    </DevModeComponentsProvider>
                                                                                                </DevModeStateForSearchProvider>
                                                                                            </FeatureFlagsStateProvider>
                                                                                        </SimpleTranslationProvider>
                                                                                    </TranslationUsedAndNotFoundProvider>
                                                                                </LanguageProvider>
                                                                            </AuthenticationProvider>
                                                                        </DebugStateProvider>
                                                                    </AiAssistantProvider>
                                                                </FilterProvider>
                                                            </FilterScaffoldingProvider>
                                                        </SearchResultsProvider>
                                                    </GuiFilterProvider>

                                                </AccessTokenStoreProvider>
                                            </SearchParserConfigProvider>
                                        </WindowUrlProvider>
                                    </DisplayFeedbackComponentProvider>
                                </CryptoStoreProvider>
                            </StorageProvider>
                        </QuestionsSuggestionsProvider>
                    </LoadingFlagProvider>
                </MsalProvider>
            </React.StrictMode >,
        )
            ;
    });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
